import { Alert, Box, FormControlLabel, Switch, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AutoPayDto, CouponRedemptionDto } from '@app/shared';
import { DataTable } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { ErrorState, LoadingState } from '../../components/PageState';
import { api } from '../../lib/api';
import { formatCents, formatDate, formatDateTime } from '../../lib/format';
import { useCustomerContext } from './CustomerLayout';
import { Section } from './parts';

const COUPON_COLUMNS: Column<CouponRedemptionDto>[] = [
  { key: '#', header: '#', width: 50, render: (_r, i) => i + 1 },
  { key: 'code', header: 'Coupon Code', nowrap: true, render: (r) => r.code },
  { key: 'name', header: 'Coupon Name', render: (r) => r.couponName },
  { key: 'on', header: 'Applied On', render: (r) => r.appliedOn },
  {
    key: 'discount',
    header: 'Discount',
    align: 'right',
    render: (r) => formatCents(r.discountCents),
  },
  { key: 'invoice', header: 'Invoice No', nowrap: true, render: (r) => r.invoiceNo },
  { key: 'at', header: 'Date', nowrap: true, render: (r) => formatDateTime(r.at) },
  { key: 'by', header: 'Applied By', render: (r) => r.by?.loginId ?? '' },
];

/** "Coupons log" of Billing and Invoice (PC1195): the coupons this customer has used. */
export function CouponsLog() {
  const { customer } = useCustomerContext();
  const log = useQuery({
    queryKey: ['customers', customer.id, 'coupons'],
    queryFn: () => api.get<{ data: CouponRedemptionDto[] }>(`/customers/${customer.id}/coupons`),
    select: (r) => r.data,
  });
  return (
    <Section title="Coupons log (PC1195)">
      <DataTable
        label="Coupons log"
        columns={COUPON_COLUMNS}
        rows={log.data}
        rowKey={(r) => r.id}
        loading={log.isFetching}
        error={log.error}
        onRetry={() => void log.refetch()}
        emptyText="This customer has used no coupon."
      />
    </Section>
  );
}

/**
 * Auto Pay (PC574). The reference charges a saved card on a day of the month; no payment
 * gateway is connected here, so Auto Pay is one switch: renew the plan from the wallet when
 * it expires. It acts only while "Act on expired plans" is on in System Config.
 */
export function AutoPayPage() {
  const { customer } = useCustomerContext();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const path = `/customers/${customer.id}/autopay`;
  const screen = useQuery({
    queryKey: ['customers', customer.id, 'autopay'],
    queryFn: () => api.get<AutoPayDto>(path),
  });
  const save = useMutation({
    mutationFn: (enabled: boolean) => api.put<AutoPayDto>(path, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['customers', customer.id] }),
  });
  const toggle = async (enabled: boolean) => {
    try {
      await save.mutateAsync(enabled);
      notify.success(enabled ? 'Auto Pay switched on.' : 'Auto Pay cancelled.');
    } catch (err) {
      notify.error(err);
    }
  };
  const data = screen.data;
  const short = Boolean(data?.plan && data.walletBalanceCents < data.plan.priceCents);

  return (
    <Section title="Auto Pay (PC574)">
      {screen.error ? (
        <ErrorState error={screen.error} onRetry={() => void screen.refetch()} />
      ) : !data ? (
        <LoadingState />
      ) : !data.plan ? (
        <Alert severity="info">This account has no plan to renew.</Alert>
      ) : (
        <Box aria-label="Auto Pay">
          <Typography sx={{ mb: 1 }}>
            Plan: <strong>{data.plan.name}</strong> ({formatCents(data.plan.priceCents)}). Expires{' '}
            {formatDate(data.planExpiresAt) || 'not set'}. Wallet balance{' '}
            <strong>{formatCents(data.walletBalanceCents)}</strong>.
          </Typography>
          <FormControlLabel
            label="Renew the plan from the wallet when it expires"
            control={
              <Switch
                checked={data.enabled}
                disabled={save.isPending}
                onChange={(e) => void toggle(e.target.checked)}
              />
            }
          />
          {data.enabled && data.since ? (
            <Typography variant="caption" color="text.secondary" display="block">
              On since {formatDateTime(data.since)}.
            </Typography>
          ) : null}
          {data.enabled && short ? (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              The wallet holds less than the plan price. Auto Pay will fail unless money is added to
              the wallet before the plan expires.
            </Alert>
          ) : null}
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Auto Pay can only use the wallet: no card can be charged, because no payment gateway is
            connected. It acts when &quot;Act on expired plans&quot; is on in System Config.
          </Alert>
        </Box>
      )}
    </Section>
  );
}
